import { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { auth, db } from "../../firebase/firebase.js";
import { API_BASE } from "../../config/api.js";
import styles from "./Admin.module.css";

async function getIdToken() {
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");
  return user.getIdToken();
}

async function adminDelete(path) {
  const idToken = await getIdToken();
  const resp = await fetch(`${API_BASE}${path}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${idToken}` },
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
  return data;
}

function displayName(row) {
  const name = [row.firstName, row.lastName].filter(Boolean).join(" ").trim();
  return name || row.email || row.id;
}

function matchesSearch(row, search) {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  const hay = [
    row.firstName,
    row.lastName,
    row.email,
    row.id,
    displayName(row),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}

function monthBounds(monthValue) {
  // monthValue: "YYYY-MM"
  const [y, m] = monthValue.split("-").map(Number);
  const start = new Date(y, m - 1, 1, 0, 0, 0, 0);
  const end = new Date(y, m, 1, 0, 0, 0, 0);
  return { start, end };
}

function formatEventWhen(value) {
  if (!value) return "—";
  const date = value?.toDate?.() || new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function currentMonthValue() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function PersonList({
  title,
  people,
  search,
  onSearchChange,
  selectedId,
  onSelect,
  loading,
  emptyLabel,
}) {
  const filtered = useMemo(
    () => people.filter((p) => matchesSearch(p, search)),
    [people, search]
  );

  return (
    <div className={styles.panel}>
      <h2>{title}</h2>
      <div className={styles.row}>
        <input
          className={styles.input}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search name or email…"
          aria-label={`Search ${title.toLowerCase()}`}
        />
      </div>
      {loading ? (
        <p className={styles.muted}>Loading…</p>
      ) : filtered.length === 0 ? (
        <p className={styles.muted}>{emptyLabel}</p>
      ) : (
        <ul className={styles.pickList}>
          {filtered.map((person) => {
            const selected = person.id === selectedId;
            return (
              <li key={person.id}>
                <button
                  type="button"
                  className={selected ? styles.pickItemActive : styles.pickItem}
                  onClick={() => onSelect(person)}
                >
                  <span className={styles.pickName}>{displayName(person)}</span>
                  <span className={styles.pickMeta}>
                    {person.email || "no email"} · {person.id}
                    {person.status ? ` · ${person.status}` : ""}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function EventsMonthPanel({
  personLabel,
  personId,
  roleField,
  month,
  onMonthChange,
  onDeleted,
  studentFilterOptions = null,
  studentFilterId = "",
  onStudentFilterChange = null,
}) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const loadEvents = useCallback(async () => {
    if (!personId || !month) return;
    setLoading(true);
    setError("");
    try {
      const { start, end } = monthBounds(month);
      const q = query(
        collection(db, "events"),
        where(roleField, "==", personId),
        where("start", ">=", Timestamp.fromDate(start)),
        where("start", "<", Timestamp.fromDate(end))
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const aMs = a.start?.toMillis?.() || 0;
        const bMs = b.start?.toMillis?.() || 0;
        return aMs - bMs;
      });
      setEvents(rows);
    } catch (err) {
      setError(err.message || "Failed to load events");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [personId, month, roleField]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const filterOptions = useMemo(() => {
    if (!studentFilterOptions) return null;
    const byId = new Map();
    for (const s of studentFilterOptions) {
      if (s?.id) byId.set(s.id, s);
    }
    for (const ev of events) {
      const id = ev.studentID;
      if (!id || byId.has(id)) continue;
      byId.set(id, {
        id,
        firstName: ev.studentName || "",
        lastName: "",
        email: "",
      });
    }
    return [...byId.values()].sort((a, b) =>
      displayName(a).localeCompare(displayName(b), undefined, {
        sensitivity: "base",
      })
    );
  }, [studentFilterOptions, events]);

  const visibleEvents = useMemo(() => {
    if (!studentFilterId) return events;
    return events.filter((ev) => ev.studentID === studentFilterId);
  }, [events, studentFilterId]);

  const deleteEvent = async (eventId) => {
    if (!window.confirm(`Delete lesson "${eventId}"? This cannot be undone.`)) {
      return;
    }
    setBusyId(eventId);
    setError("");
    try {
      await adminDelete(`/admin/events/${encodeURIComponent(eventId)}`);
      setEvents((prev) => prev.filter((e) => e.id !== eventId));
      onDeleted?.(`Event ${eventId} deleted`);
    } catch (err) {
      setError(err.message || "Failed to delete event");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={styles.subPanel}>
      <h3>Lessons for {personLabel}</h3>
      <p className={styles.muted}>
        Pick a month to list past and future lessons. Delete individually.
      </p>
      <div className={styles.row}>
        <input
          className={styles.input}
          type="month"
          value={month}
          onChange={(e) => onMonthChange(e.target.value)}
        />
        {filterOptions && onStudentFilterChange && (
          <select
            className={styles.select}
            value={studentFilterId}
            onChange={(e) => onStudentFilterChange(e.target.value)}
            aria-label="Filter by student"
          >
            <option value="">All students</option>
            {filterOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {displayName(s)}
                {s.email ? ` (${s.email})` : ""}
              </option>
            ))}
          </select>
        )}
        <button type="button" className={styles.button} onClick={loadEvents}>
          Refresh
        </button>
      </div>
      {error && <p className={styles.error}>{error}</p>}
      {loading ? (
        <p className={styles.muted}>Loading lessons…</p>
      ) : visibleEvents.length === 0 ? (
        <p className={styles.muted}>
          {events.length > 0 && studentFilterId
            ? "No lessons with this student in this month."
            : "No lessons in this month."}
        </p>
      ) : (
        <ul className={styles.list}>
          {visibleEvents.map((ev) => (
            <li key={ev.id}>
              <div>
                <strong>{formatEventWhen(ev.start)}</strong>
                <div className={styles.muted}>
                  {ev.studentName || ev.studentID || "student"} ↔{" "}
                  {ev.teacherName || ev.teacherID || "teacher"}
                </div>
                <div className={styles.muted}>{ev.id}</div>
              </div>
              <button
                type="button"
                className={styles.buttonDanger}
                disabled={busyId === ev.id}
                onClick={() => deleteEvent(ev.id)}
              >
                {busyId === ev.id ? "Deleting…" : "Delete lesson"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminDeletes() {
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [teacherSearch, setTeacherSearch] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [teacherMonth, setTeacherMonth] = useState(currentMonthValue);
  const [studentMonth, setStudentMonth] = useState(currentMonthValue);
  const [linkedStudents, setLinkedStudents] = useState([]);
  const [teacherStudentFilterId, setTeacherStudentFilterId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const loadTeachers = useCallback(async () => {
    setLoadingTeachers(true);
    try {
      const snap = await getDocs(collection(db, "teachers"));
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) =>
        displayName(a).localeCompare(displayName(b), undefined, {
          sensitivity: "base",
        })
      );
      setTeachers(rows);
    } catch (err) {
      setError(err.message || "Failed to load teachers");
      setTeachers([]);
    } finally {
      setLoadingTeachers(false);
    }
  }, []);

  const loadStudents = useCallback(async () => {
    setLoadingStudents(true);
    try {
      const snap = await getDocs(collection(db, "students"));
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) =>
        displayName(a).localeCompare(displayName(b), undefined, {
          sensitivity: "base",
        })
      );
      setStudents(rows);
    } catch (err) {
      setError(err.message || "Failed to load students");
      setStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  }, []);

  useEffect(() => {
    loadTeachers();
    loadStudents();
  }, [loadTeachers, loadStudents]);

  useEffect(() => {
    let cancelled = false;
    const loadLinks = async () => {
      if (!selectedTeacher) {
        setLinkedStudents([]);
        return;
      }
      try {
        const snap = await getDocs(
          query(
            collection(db, "teacherStudentLinks"),
            where("teacherId", "==", selectedTeacher.id)
          )
        );
        if (cancelled) return;
        const byId = new Map(students.map((s) => [s.id, s]));
        const linked = snap.docs
          .map((d) => d.data().studentId)
          .filter(Boolean)
          .map((id) => byId.get(id) || { id, firstName: "", lastName: "", email: "" });
        setLinkedStudents(linked);
      } catch {
        if (!cancelled) setLinkedStudents([]);
      }
    };
    loadLinks();
    return () => {
      cancelled = true;
    };
  }, [selectedTeacher, students]);

  const deleteTeacher = async () => {
    if (!selectedTeacher) return;
    const name = displayName(selectedTeacher);
    if (
      !window.confirm(
        `Delete teacher "${name}"?\n\nThis removes the profile, Auth user, teacher–student links, and all future lessons (with Daily rooms). Past lessons stay unless you delete them below.`
      )
    ) {
      return;
    }
    setBusy("teacher");
    setError("");
    setMessage("");
    try {
      const result = await adminDelete(
        `/admin/teachers/${encodeURIComponent(selectedTeacher.id)}`
      );
      setMessage(result.message || "Teacher deleted");
      setTeachers((prev) => prev.filter((t) => t.id !== selectedTeacher.id));
      setSelectedTeacher(null);
      setTeacherStudentFilterId("");
    } catch (err) {
      setError(err.message || "Failed to delete teacher");
    } finally {
      setBusy("");
    }
  };

  const deleteStudent = async () => {
    if (!selectedStudent) return;
    const name = displayName(selectedStudent);
    if (
      !window.confirm(
        `Delete student "${name}"?\n\nThis removes the profile, Auth user, comments, and teacher links. Lesson events are left in place (delete them from a teacher view if needed).`
      )
    ) {
      return;
    }
    setBusy("student");
    setError("");
    setMessage("");
    try {
      const result = await adminDelete(
        `/admin/students/${encodeURIComponent(selectedStudent.id)}`
      );
      setMessage(result.message || "Student deleted");
      setStudents((prev) => prev.filter((s) => s.id !== selectedStudent.id));
      setSelectedStudent(null);
    } catch (err) {
      setError(err.message || "Failed to delete student");
    } finally {
      setBusy("");
    }
  };

  return (
    <div>
      <h1 className={styles.title}>Deletes</h1>
      <p className={styles.lead}>
        Search teachers or students by name or email. Deleting a teacher also
        removes their future lessons. Use the month list to delete individual
        lessons (past or future).
      </p>

      {error && <p className={styles.error}>{error}</p>}
      {message && <p className={styles.success}>{message}</p>}

      <div className={styles.split}>
        <PersonList
          title="Teachers"
          people={teachers}
          search={teacherSearch}
          onSearchChange={setTeacherSearch}
          selectedId={selectedTeacher?.id}
          onSelect={(person) => {
            setSelectedTeacher(person);
            setSelectedStudent(null);
            setTeacherStudentFilterId("");
            setError("");
            setMessage("");
          }}
          loading={loadingTeachers}
          emptyLabel="No teachers match."
        />
        <PersonList
          title="Students"
          people={students}
          search={studentSearch}
          onSearchChange={setStudentSearch}
          selectedId={selectedStudent?.id}
          onSelect={(person) => {
            setSelectedStudent(person);
            setSelectedTeacher(null);
            setTeacherStudentFilterId("");
            setError("");
            setMessage("");
          }}
          loading={loadingStudents}
          emptyLabel="No students match."
        />
      </div>

      {selectedTeacher && (
        <div className={styles.panel}>
          <h2>Selected teacher</h2>
          <p>
            <strong>{displayName(selectedTeacher)}</strong>
          </p>
          <p className={styles.muted}>
            {selectedTeacher.email || "no email"} · {selectedTeacher.id}
          </p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.buttonDanger}
              disabled={busy === "teacher"}
              onClick={deleteTeacher}
            >
              {busy === "teacher"
                ? "Deleting…"
                : "Delete teacher + future lessons"}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => {
                setSelectedTeacher(null);
                setTeacherStudentFilterId("");
              }}
            >
              Clear
            </button>
          </div>
          <EventsMonthPanel
            personLabel={displayName(selectedTeacher)}
            personId={selectedTeacher.id}
            roleField="teacherID"
            month={teacherMonth}
            onMonthChange={setTeacherMonth}
            onDeleted={setMessage}
            studentFilterOptions={linkedStudents}
            studentFilterId={teacherStudentFilterId}
            onStudentFilterChange={setTeacherStudentFilterId}
          />
        </div>
      )}

      {selectedStudent && (
        <div className={styles.panel}>
          <h2>Selected student</h2>
          <p>
            <strong>{displayName(selectedStudent)}</strong>
          </p>
          <p className={styles.muted}>
            {selectedStudent.email || "no email"} · {selectedStudent.id}
          </p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.buttonDanger}
              disabled={busy === "student"}
              onClick={deleteStudent}
            >
              {busy === "student" ? "Deleting…" : "Delete student"}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => setSelectedStudent(null)}
            >
              Clear
            </button>
          </div>
          <EventsMonthPanel
            personLabel={displayName(selectedStudent)}
            personId={selectedStudent.id}
            roleField="studentID"
            month={studentMonth}
            onMonthChange={setStudentMonth}
            onDeleted={setMessage}
          />
        </div>
      )}
    </div>
  );
}
